import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useRef, useState } from 'react';

interface CustomToolTipProps {
    children: React.ReactNode;
    title: React.ReactNode;
    placement?: 'bottom' | 'top' | 'left' | 'right' |
    'bottom-start' | 'bottom-end' |
    'top-start' | 'top-end' |
    'left-start' | 'left-end' |
    'right-start' | 'right-end';
    disabled?: boolean;
    className?: string;
    svg?: React.ReactNode
    maxWidth?: string
    wordBreak?: React.CSSProperties['wordBreak']
    style?: React.CSSProperties
}

const CustomToolTip: React.FC<CustomToolTipProps> = ({
    children,
    title,
    placement = 'top',
    disabled = false,
    className,
    svg,
    maxWidth,
    wordBreak = 'normal',
    style,
}) => {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLSpanElement>(null);

    // Utility to find the closest scrollable parent
    const getScrollParent = (element: HTMLElement | null): HTMLElement | null => {
        while (element) {
            const overflowY = window.getComputedStyle(element).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') {
                return element;
            }
            element = element.parentElement;
        }
        return null;
    };

    useEffect(() => {
        const triggerEl = triggerRef.current;
        if (!triggerEl) return;

        const scrollParent = getScrollParent(triggerEl);

        const handleScroll = () => {
            setOpen(false); // Instant close
        };

        const handleClick = () => {
            setOpen(false); // Instant close on click (capture phase)
        };

        scrollParent?.addEventListener('scroll', handleScroll);
        triggerEl.addEventListener('click', handleClick, true);

        return () => {
            scrollParent?.removeEventListener('scroll', handleScroll);
            triggerEl.removeEventListener('click', handleClick, true);
        };
    }, [triggerRef.current]);

    return (
        <Tooltip
            title={
                <div className='d-flex align-items-center gap-2'>
                    {svg && <span>{svg}</span>}
                    <span style={{
                        wordBreak
                    }}>{title}</span>
                </div>
            }
            placement={placement}
            open={open && !disabled && title !== ''}
            disableInteractive
            disableFocusListener
            disableHoverListener
            leaveDelay={0}
            PopperProps={{
                style: {
                    zIndex: 100000,
                    pointerEvents: 'none',
                },
                modifiers: [
                    {
                        name: "offset",
                        options: {
                            offset: [0, 0], // [skidding, distance]
                            // skidding: horizontal offset, distance: vertical offset
                        },
                    },
                ],
            }}
            componentsProps={{
                tooltip: {
                    sx: {
                        display: disabled ? 'none' : 'flex',
                        color: "#eee",
                        backgroundColor: "rgba(9, 9, 9, 0.6) !important",
                        backdropFilter: "blur(20px)",
                        padding: '6px 8px',
                        fontSize: '12px',
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 400,
                        lineHeight: '16px',
                        letterSpacing: '0.096px',
                        borderRadius: '4px',
                        border: '0.496px solid rgba(240, 240, 240, 0.15)',
                        maxWidth: maxWidth || '700px',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        wordBreak: wordBreak || 'normal',
                        overflow: 'hidden',
                        zIndex: 99999,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                    },
                },
            }}
        >
            <span
                className={`${className || ''}`}
                ref={triggerRef}
                style={style}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
            >
                {children}
            </span>
        </Tooltip>
    );
};

export default CustomToolTip;
